import { JsonDiscriminatorProperty, JsonElementType, JsonObject, JsonProperty } from "ta-json";

import type { MapCultureTo } from "../../base-types";
import DataType from "./data-type";
import { IMemberCondition, MemberCondition } from "./member-condition";
import { MemberDefinitionType } from "./member-definition-type";
import { IPropertyDefinition } from "./property-definition";
import {
    BooleanPropertyDefinition,
    DateTimeOffsetPropertyDefinition,
    DateTimePropertyDefinition,
    DecimalPropertyDefinition,
    IntegerPropertyDefinition,
    JsonPropertyDefinition,
    LongPropertyDefinition,
    StringPropertyDefinition,
} from "./property-definitions";
import { IRelationDefinition } from "./relation-definition";

/**
 * Base interface for all member definitions.
 */
export interface IMemberDefinition {
    /**
     * Data type of the member.
     */
    readonly type: DataType;

    /**
     * Type of the member definition.
     */
    readonly definitionType: MemberDefinitionType;

    /**
     * Language agnostic case insensitive name of the member.
     * Must be unique per member and {@link IEntityDefinition}, except for self-referencing relations.
     */
    name: string;

    /**
     * Indicates whether this member can be updated from external sources (e.g. REST API, SDK...).
     */
    allowUpdates: boolean;

    /**
     * Indicates whether this member definition has any member conditions.
     */
    readonly isConditional: boolean;

    /**
     * The conditions that are evaluated to decide if this member should be exposed in the external API/UI.
     * These conditions have no impact on data validity.
     */
    conditions: Array<IMemberCondition>;

    /**
     * Indicates if the member definition is owned by the system and cannot be modified or deleted by the regular users.
     */
    isSystemOwned: boolean;

    /**
     * Indicates if the read-write access to this member is restricted.
     */
    isSecured: boolean;

    /**
     * Collection of the culture specific member labels.
     */
    labels: MapCultureTo<string>;

    /**
     * Collection of the culture specific help text.
     */
    helpText: MapCultureTo<string>;

    /**
     * Indicates whether this member definition can trigger conditional members.
     */
    canTriggerConditionalMembers: boolean;

    /**
     * Indicates whether this member definition can write.
     */
    canWrite: boolean;
}

@JsonObject()
@JsonDiscriminatorProperty("type")
export abstract class MemberDefinition implements IMemberDefinition {
    @JsonProperty("type")
    readonly type: DataType;

    @JsonProperty("name")
    name: string;

    @JsonProperty("allow_updates")
    allowUpdates: boolean = true;

    @JsonProperty("is_system_owned")
    isSystemOwned: boolean;

    @JsonProperty("is_secured")
    isSecured: boolean;

    @JsonProperty("labels")
    labels: MapCultureTo<string> = {};

    @JsonProperty("helptext")
    helpText: MapCultureTo<string> = {};

    @JsonProperty("conditions")
    @JsonElementType(MemberCondition)
    conditions: Array<IMemberCondition> = [];

    @JsonProperty("can_trigger_conditional_members")
    canTriggerConditionalMembers: boolean;

    @JsonProperty("can_write")
    canWrite: boolean;

    get isConditional(): boolean {
        return !!this.conditions.length;
    }

    abstract get definitionType(): MemberDefinitionType;

    constructor(name: string, type: DataType) {
        this.name = name;
        this.type = type;
    }

    static isPropertyDefinition(definition: IMemberDefinition): definition is IPropertyDefinition {
        return definition.definitionType === MemberDefinitionType.Property;
    }

    static isStringPropertyDefinition(definition: IMemberDefinition): definition is StringPropertyDefinition {
        return this.isPropertyDefinition(definition) && definition.dataType === DataType.String;
    }

    static isIntegerPropertyDefinition(definition: IMemberDefinition): definition is IntegerPropertyDefinition {
        return this.isPropertyDefinition(definition) && definition.dataType === DataType.Integer;
    }

    static isLongPropertyDefinition(definition: IMemberDefinition): definition is LongPropertyDefinition {
        return this.isPropertyDefinition(definition) && definition.dataType === DataType.Long;
    }

    static isDecimalPropertyDefinition(definition: IMemberDefinition): definition is DecimalPropertyDefinition {
        return this.isPropertyDefinition(definition) && definition.dataType === DataType.Decimal;
    }

    static isBooleanPropertyDefinition(definition: IMemberDefinition): definition is BooleanPropertyDefinition {
        return this.isPropertyDefinition(definition) && definition.dataType === DataType.Boolean;
    }

    static isDateTimePropertyDefinition(definition: IMemberDefinition): definition is DateTimePropertyDefinition {
        return this.isPropertyDefinition(definition) && definition.dataType === DataType.DateTime;
    }

    static isDateTimeOffsetPropertyDefinition(
        definition: IMemberDefinition
    ): definition is DateTimeOffsetPropertyDefinition {
        return this.isPropertyDefinition(definition) && definition.dataType === DataType.DateTimeOffset;
    }

    static isJsonPropertyDefinition(definition: IMemberDefinition): definition is JsonPropertyDefinition {
        return this.isPropertyDefinition(definition) && definition.dataType === DataType.Json;
    }

    static isRelationDefinition(definition: IMemberDefinition): definition is IRelationDefinition {
        return definition.definitionType === MemberDefinitionType.Relation;
    }
}
